import React from 'react';
import { Check } from 'lucide-react';
import { IMG } from './shared';

const OFFERINGS = [
  {
    eyebrow: 'Telematics tracking',
    title: 'Track vehicles on trip.',
    copy: 'Connect your telematics to view every vehicle live on the map and stay in the loop from pickup to return.',
    points: ['Real-time GPS location per trip', 'Route history with every stop', 'Instant alerts for unusual activity'],
    image: `${IMG}/inshur/telematics-tracking.webp`,
    alt: 'Over-the-shoulder view of a person watching a live vehicle-tracking map on their phone with a location pin and route stops',
  },
  {
    eyebrow: 'Roadside assistance',
    title: 'Help on the road, built in.',
    copy: 'Give every renter 24/7 roadside support so a breakdown never becomes your problem.',
    points: ['Tow, jump-start, lockout services', 'Tire change and fuel replacement', 'Leisure and ride share plans available'],
    image: `${IMG}/inshur/roadside-assistance.webp`,
    alt: 'A roadside assistance technician helping a driver next to an SUV parked on the shoulder at golden hour',
  },
  {
    eyebrow: 'Tolls management',
    title: 'Tolls, tracked and billed.',
    copy: "Every toll is automatically captured and passed through to the renter's bill. No more manual lookups or paperwork.",
    points: ['Automatic toll capture per trip', 'Passed through to the renter', 'Reconciled in one dashboard'],
    image: `${IMG}/inshur/tolls-management.webp`,
    alt: 'A car driving through an open-road electronic highway toll gantry on a clear day',
  },
];

const OfferingCard = ({ offering }) => (
  <div className="mpa-offering-card">
    <img src={offering.image} alt={offering.alt} className="mpa-oc-img" loading="lazy" />

    {/* Expand indicator, top-right */}
    <span aria-hidden="true" className="mpa-oc-expand">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <polyline points="14 4 20 4 20 10" className="mpa-oc-expand-tr" />
        <polyline points="10 20 4 20 4 14" className="mpa-oc-expand-bl" />
      </svg>
    </span>

    {/* Category label in the bottom-left corner (fades out on hover) */}
    <div className="mpa-oc-label">
      <span className="mpa-oc-dot" aria-hidden="true" />
      <p>{offering.eyebrow}</p>
    </div>

    {/* Detail panel — slides up over the image on hover */}
    <div className="mpa-oc-panel">
      <div className="mpa-oc-panel-kicker">
        <span className="mpa-oc-dot" aria-hidden="true" />
        <p>{offering.eyebrow}</p>
      </div>
      <h3 className="mpa-oc-title">{offering.title}</h3>
      <p className="mpa-oc-copy">{offering.copy}</p>
      <ul className="mpa-oc-points">
        {offering.points.map((p) => (
          <li key={p}>
            <span className="mpa-check mpa-check--sm">
              <Check size={10} strokeWidth={3} />
            </span>
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

/** Roadside, tolls, and telematics — three hover-reveal cards. */
const RoadsideSection = () => (
  <section className="mpa-roadside">
    <div className="mpa-container">
      <div className="mpa-roadside-inner">
        <div className="mpa-leadin mpa-roadside-lead">
          <p className="mpa-eyebrow">Automated Operational Workflows</p>
          <h2 className="mpa-roadside-title">Roadside, tolls, and telematics, built in to every trip</h2>
          <p className="mpa-leadin-sub mpa-leadin-sub--lg">
            Automated roadside assistance, toll reconciliation, and integrated vehicle telematics.
          </p>
        </div>

        <div className="mpa-roadside-cards">
          {OFFERINGS.map((o) => (
            <OfferingCard key={o.eyebrow} offering={o} />
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default RoadsideSection;
